// Global error handler - filters out benign errors
window.addEventListener('error', function(event) {
  // Filter out "Script error" which is a generic CORS error and not actionable
  const errorMessage = event.error?.message || event.message || '';
  const errorString = String(errorMessage);
  
  // Skip logging generic script errors (usually CORS-related)
  if (errorString === 'Script error.' || errorString === 'Script error') {
    return; // Silently ignore - not actionable
  }
  
  // Skip logging errors from external scripts (cross-origin)
  if (event.filename && !event.filename.includes(window.location.origin) && !event.filename.includes('localhost')) {
    return; // Silently ignore external script errors
  }
  
  // Only log meaningful errors
  console.error('Global error:', {
    message: errorMessage,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  });
});

window.addEventListener('unhandledrejection', function(event) {
  const reason = event.reason;
  const reasonString = String(reason?.message || reason || '');
  
  // Skip logging generic script errors
  if (reasonString === 'Script error.' || reasonString === 'Script error') {
    return; // Silently ignore
  }
  
  // Only log meaningful rejections
  console.error('Unhandled promise rejection:', {
    reason: reason,
    message: reason?.message || String(reason)
  });
});

import { supabase, handleSupabaseError, getCurrentUser } from '@/lib/supabase'

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in miles
};

// Enhanced spatial search for camps
export const searchCampsNearby = async (latitude, longitude, radiusMiles = 25, filters = {}) => {
  try {
    // First try PostGIS function if available
    try {
      let query = supabase.rpc('camps_within_radius', {
        center_lat: latitude,
        center_lng: longitude,
        radius_miles: radiusMiles
      })

      // Apply additional filters - updated for camp_audit_data
      if (filters.ageMin && filters.ageMax) {
        // Note: age_min/age_max don't exist in camp_audit_data
        // Would need to parse age_range field
      }

      if (filters.category) {
        query = query.eq('camp_type', filters.category)
      }

      if (filters.maxPrice) {
        query = query.lte('cost', filters.maxPrice)
      }

      // Note: start_date/end_date don't exist in camp_audit_data

      const { data, error } = await query.limit(50)

      if (!error && data) {
        return data
      }
    } catch (postgisError) {
      console.log('PostGIS function not available, falling back to JavaScript calculation')
    }

    // Fallback: Get all camps and filter by distance in JavaScript
    console.log('🔍 Fetching all camps from database...');
    const { data: allCamps, error } = await supabase
      .from('camp_audit_data')
      .select('*')
      .limit(100)

    if (error) {
      console.error('❌ Database error:', error);
      throw error;
    }
    
    console.log('📊 Found camps in database:', allCamps?.length || 0, allCamps);

    // Filter camps by distance and other criteria
    const nearbyCamps = allCamps
      .filter(camp => {
        // Use coordinates_lat and coordinates_lng from camp_audit_data
        const campLat = camp.coordinates_lat;
        const campLng = camp.coordinates_lng;
        
        if (!campLat || !campLng) {
          console.log('Camp missing coordinates:', camp.name, { lat: campLat, lng: campLng });
          return false;
        }
        
        // Calculate distance
        const distance = calculateDistance(latitude, longitude, campLat, campLng)
        
        // Apply distance filter
        if (distance > radiusMiles) return false
        
        // Apply age filters - camp_audit_data uses age_range (text field)
        // For now, skip age filtering since we'd need to parse the age_range text
        // TODO: Implement age_range parsing if needed
        
        // Apply category filter - use camp_type instead of category
        if (filters.category && camp.camp_type !== filters.category) return false
        
        // Apply price filter - use cost instead of price
        if (filters.maxPrice && camp.cost && camp.cost > filters.maxPrice) return false
        
        return true
      })
      .map(camp => ({
        ...camp,
        distance_miles: calculateDistance(latitude, longitude, camp.coordinates_lat, camp.coordinates_lng)
      }))
      .sort((a, b) => a.distance_miles - b.distance_miles) // Sort by distance
      .slice(0, 50) // Limit to 50 results

    return nearbyCamps
  } catch (error) {
    console.error('Spatial search error:', error)
    throw error
  }
}

// Get nearest camps (simple proximity search)
export const getNearestCamps = async (latitude, longitude, limit = 10) => {
  try {
    const { data, error } = await supabase.rpc('nearest_camps', {
      center_lat: latitude,
      center_lng: longitude,
      limit_count: limit
    })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Nearest camps error:', error)
    handleSupabaseError(error)
  }
}

// Enhanced getUserLocation function
export const getUserLocation = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Validate coordinates
        if (typeof lat !== 'number' || typeof lng !== 'number' || 
            isNaN(lat) || isNaN(lng) || 
            lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          reject(new Error('Invalid coordinates received from geolocation'));
          return;
        }
        
        console.log('GPS location detected:', { lat, lng });
        resolve({
          latitude: lat,
          longitude: lng,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
      },
      async (error) => {
        console.error('GPS geolocation error:', error);
        
        // Fallback to IP-based geolocation
        try {
          console.log('Attempting IP-based geolocation fallback...');
          const response = await fetch('https://ipapi.co/json/');
          const data = await response.json();
          
          if (data.latitude && data.longitude) {
            console.log('IP location detected:', { lat: data.latitude, lng: data.longitude });
            resolve({
              latitude: data.latitude,
              longitude: data.longitude,
              accuracy: 10000, // IP geolocation is less accurate
              timestamp: Date.now(),
              source: 'ip'
            });
          } else {
            reject(new Error('Unable to determine location'));
          }
        } catch (ipError) {
          console.error('IP geolocation also failed:', ipError);
          reject(new Error(`Location detection failed: ${error.message}`));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 8000, // Reduced timeout for faster fallback
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

// Google Calendar integration functions
export const startGoogleCalendarAuth = async () => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    // Always use client-side OAuth URL generation with dynamic redirect URI
    // This ensures the redirect URI matches what we'll send back in the callback
    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID
    if (!clientId) {
      throw new Error('Google OAuth Client ID not configured. Please add VITE_GOOGLE_OAUTH_CLIENT_ID to your environment variables.')
    }
    
    const redirectUri = `${window.location.origin}/profile?calendar_callback=true`
    
    // Use consolidated scopes from environment variable for single consent screen
    const scopes = (import.meta.env.VITE_GOOGLE_OAUTH_SCOPES || '').split(',').filter(scope => scope.trim());
    if (scopes.length === 0) {
      throw new Error('Google OAuth Scopes not configured. Please add VITE_GOOGLE_OAUTH_SCOPES to your environment variables.')
    }
    const scopeString = scopes.join(' ')
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scopeString)}&` +
      `response_type=code&` +
      `access_type=offline&` +
      `include_granted_scopes=true&` +
      `state=${user.id}`
    
    console.log('Generated OAuth auth URL:', authUrl)
    return { authUrl }
  } catch (error) {
    console.error('startGoogleCalendarAuth error:', error)
    handleSupabaseError(error)
  }
};

export const googleCalendarCallback = async (code) => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    console.log('Processing Google Calendar callback with code:', code)

    // Get the redirect URI that was used when initiating OAuth
    // This must match exactly what Google expects
    const redirectUri = `${window.location.origin}/profile?calendar_callback=true`

    // Check if Edge Function exists, otherwise use fallback
    try {
      console.log('Attempting to invoke google-calendar-auth callback edge function...')
      const response = await supabase.functions.invoke('google-calendar-auth', {
        body: { 
          action: 'callback',
          code: code,
          redirect_uri: redirectUri  // Pass the redirect URI used in OAuth flow
        }
      })
      
      console.log('📦 Full edge function response:', response)
      console.log('📦 Response data:', response.data)
      console.log('📦 Response error:', response.error)
      
      // If there's an error, log everything we can
      if (response.error) {
        console.error('❌ Edge function error:', response.error)
        console.error('❌ Error message:', response.error.message)
        console.error('❌ Error context:', response.error.context)
        console.error('❌ Response data (may contain error details):', response.data)
        
        // Try to get more details from response.data
        const errorMessage = response.data?.error || response.data?.details || response.error.message
        throw new Error(errorMessage)
      }

      // Check if data itself contains an error
      if (response.data?.error) {
        console.error('❌ Edge function returned error in data:', response.data)
        throw new Error(response.data.error)
      }

      const { data, error } = response
      console.log('✅ Edge function callback response:', data)

      // Update user profile with calendar connection status
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({ 
          id: user.id, 
          google_calendar_connected: true,
          google_calendar_token: data.access_token,
          google_calendar_refresh_token: data.refresh_token
        })

      if (profileError) {
        console.error('Error updating profile:', profileError)
        throw profileError
      }

      return data
    } catch (edgeFunctionError) {
      console.error('❌ Edge Function failed:', edgeFunctionError.message)
      console.error('Full error:', edgeFunctionError)
      
      // Don't use fallback - throw the error so user sees it
      throw new Error(`Failed to connect calendar: ${edgeFunctionError.message}`)
    }
  } catch (error) {
    console.error('googleCalendarCallback error:', error)
    handleSupabaseError(error)
  }
};

export const getCalendarEvents = async (startDate = null, endDate = null) => {
  try {
    console.log('📅 getCalendarEvents called:', { startDate, endDate })
    
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')
    console.log('✅ User authenticated:', user.id)

    // Get user's calendar token from profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('google_calendar_token, google_calendar_refresh_token, google_calendar_connected')
      .eq('id', user.id)
      .single()

    console.log('📊 Profile data:', {
      hasToken: !!profile?.google_calendar_token,
      tokenPreview: profile?.google_calendar_token?.substring(0, 20) + '...',
      isConnected: profile?.google_calendar_connected
    })

    if (profileError) throw profileError
    if (!profile.google_calendar_connected) {
      throw new Error('Google Calendar not connected')
    }

    // Check if Edge Function exists, otherwise use fallback
    try {
      console.log('🚀 Invoking google-calendar-events edge function...')
      console.log('📤 Request payload:', {
        hasToken: !!profile.google_calendar_token,
        tokenPreview: profile.google_calendar_token?.substring(0, 20) + '...',
        hasRefreshToken: !!profile.google_calendar_refresh_token,
        startDate,
        endDate
      })
      
      const { data, error } = await supabase.functions.invoke('google-calendar-events', {
        body: { 
          access_token: profile.google_calendar_token,
          refresh_token: profile.google_calendar_refresh_token,
          start_date: startDate,
          end_date: endDate
        }
      })
      
      console.log('📥 Edge function response:', {
        hasData: !!data,
        hasError: !!error,
        data: data,
        error: error
      })
      
      if (error) {
        console.error('❌ Edge function returned error:', error)
        throw error
      }
      
      console.log('✅ Calendar events fetched successfully:', data?.events?.length || 0, 'events')
      return data
    } catch (edgeFunctionError) {
      console.warn('⚠️ Edge Function failed, using fallback:', edgeFunctionError.message)
      console.error('Edge function error details:', {
        message: edgeFunctionError.message,
        stack: edgeFunctionError.stack,
        name: edgeFunctionError.name
      })
      
      // Fallback: Return empty events
      return { events: [] }
    }
  } catch (error) {
    console.error('❌ getCalendarEvents error:', error)
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      details: error.details
    })
    
    // Return empty events array instead of throwing
    return { events: [] }
  }
};

export const disconnectGoogleCalendar = async () => {
  try {
    const user = await getCurrentUser()
    if (!user) throw new Error('User not authenticated')

    // Revoke access and update profile
    const { error: revokeError } = await supabase.functions.invoke('google-calendar-auth', {
      body: { action: 'revoke' }
    })

    // Update profile regardless of revoke result
    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        google_calendar_connected: false,
        google_calendar_token: null
      })
      .eq('id', user.id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    handleSupabaseError(error)
  }
};

// Enhanced search camps with spatial and text search capabilities
export const searchCamps = async (filters = {}) => {
  try {
    // If location coordinates are provided, use spatial search
    if (filters.latitude && filters.longitude) {
      return await searchCampsNearby(
        filters.latitude,
        filters.longitude,
        filters.radius || 25,
        filters
      )
    }

    // Fallback to text-based location search
    let query = supabase.from('camp_audit_data').select('*')

    // Apply filters - updated field names for camp_audit_data
    if (filters.location) {
      query = query.ilike('location', `%${filters.location}%`)
    }
    if (filters.ageMin && filters.ageMax) {
      // Note: age_min/age_max don't exist in camp_audit_data, using age_range instead
      // This would need to be parsed from age_range field
      query = query.ilike('age_range', `%${filters.ageMin}%`)
    }
    if (filters.category) {
      query = query.eq('camp_type', filters.category)
    }
    if (filters.maxPrice) {
      query = query.lte('cost', filters.maxPrice)
    }
    // Note: start_date/end_date don't exist in camp_audit_data
    // These would need to be handled differently or removed

    const { data, error } = await query.order('rating', { ascending: false }).limit(50)
    if (error) throw error
    return data || []
  } catch (error) {
    handleSupabaseError(error)
  }
};

export const getCampRecommendations = async (userId) => {
  try {
    // This could be enhanced with AI recommendations
    const { data, error } = await supabase.functions.invoke('get-camp-recommendations', {
      body: { user_id: userId }
    })
    
    if (error) throw error
    return data
  } catch (error) {
    // Fallback to simple recommendations based on user preferences
    console.warn('AI recommendations not available, using fallback')
    return await searchCamps({})
  }
};

